import React, { useEffect, useMemo, useRef, useState } from "react";
import { colors, frames, glyphs } from "../../../../Styles";
import { useMediaQuery } from "../../../../Styles/mediaQueries";
import {
  Attentions,
  AutoReportWidget,
  PageHeader,
  SystemControls,
} from "../../../../Components";
import { useTranslation } from "react-i18next";
import CardNumberDisplay from "../../../../Components/BaseComponents/cardNumberDisplay";
import { useResize } from "../../../../Components/_Components";
import { useParams, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../../Store";
import { Graph_QL } from "../../../../Services";
import { useSystem } from "../../../../Services/Agrisys_GqlDataCaller/SystemCalls/useSystem";
import { useSection } from "../../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";

// const SYSTEM_ID = 1;

export default function FreedaLocationFeeder() {
  const { t } = useTranslation();
  const [widgetVerticalContent, setWidgetVerticalContent] =
    useState<boolean>(false);
  const widgetResizeRef = useRef<HTMLDivElement>(null);
  const { width } = useResize(widgetResizeRef);

  // Mediaquries
  const hideCardNumberDisplays = useMediaQuery(`(max-height: 765px)`);
  const gridMaxOut = useMediaQuery(`(min-width: 1845px)`);
  const gridReducerPrimary = useMediaQuery(`(max-width: 1260px)`);
  const gridReducerSecondary = useMediaQuery(`(max-width: 690px)`);
  const gridReducerTertiary = useMediaQuery(`(max-width: 590px)`);

  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const location = useLocation();
  const systemId = useMemo(
    () => (urlSystemId ? +urlSystemId : location?.state?.selected),
    [urlSystemId, location]
  );

  // console.log("oversigt ",systemId)

  // Handle Widget Resize
  useEffect(() => {
    if (width > 0) {
      setWidgetVerticalContent(width < 600);
    }
  }, [width]);

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { call: getSystemCall, data: systemsState } = useSystem(connection);
  const { call: getLfSectionsInfo, data: lfSectionsState } =
    useSection(connection);

  useEffect(() => {
    getSystemCall({ ids: [systemId] });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line
  }, []);

  const systemSectionsData = useMemo(() => {
    if (lfSectionsState[domain]) {
      return lfSectionsState[domain].data.filter(s => s.system_id === systemId);
    }
    return [];
  }, [domain, lfSectionsState, systemId]);

  // Get System Name for Header
  const systemName = useMemo(() => {
    const systemData = systemsState[domain]?.data.find(
      (system) => system.systemId === systemId
    );
    return systemData?.name || "";
  }, [domain, systemId, systemsState]);

  // Get System Data
  const numberOfSections = systemSectionsData.length;
  const [totalNumberOfPens, totalNumberOfAnimals] = systemSectionsData.reduce(
    (accumulator, currentSection) => {
      accumulator[0] += currentSection.section_locations.length;
      accumulator[1] += currentSection.section_locations.reduce(
        (accumulator, currentLocation) => {
          accumulator += currentLocation.animals.length;
          return accumulator;
        },
        0
      );
      return accumulator;
    },
    [0, 0]
  );

  // const mobileGrid = (
  //   <div></div>
  // )

  const CardDisplay = (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        flexDirection: "row",
        gap: "10px",
        boxSizing: "border-box",
        alignItems: "center",
        width: "320px",
      }}
    >
      <CardNumberDisplay
        value={numberOfSections}
        unit={t("locationFeederPages.locationFeeder.sections", {
          defaultValue: "Sections",
        })}
        glyph={
          <glyphs.AnimalGroup fillColor={colors.greySecondary} size="150px" />
        }
      />
      <CardNumberDisplay
        value={totalNumberOfPens}
        unit={t("locationFeederPages.locationFeeder.pens", {
          defaultValue: "Pens",
        })}
        glyph={
          <glyphs.Condition fillColor={colors.greySecondary} size="150px" />
        }
      />
      <CardNumberDisplay
        value={totalNumberOfAnimals}
        unit={t("locationFeederPages.locationFeeder.animals", {
          defaultValue: "Animals",
        })}
        glyph={<glyphs.Animal fillColor={colors.greySecondary} size="150px" />}
      />
    </div>
  );

  const FeedinWidget = (
    <AutoReportWidget
      verticalContent={widgetVerticalContent}
      header={t("feed.general.feeding", { defaultValue: "feeding" })}
      systemId={systemId}
      autoReports={[1]}
      progressCircles={[1, 2, 3]}
    />
  );
  const MilestonesWidget = (
    <AutoReportWidget
      verticalContent={widgetVerticalContent}
      header={t("milestones.general.animalsInMilestones", {
        defaultValue: "animals in milestones",
      })}
      systemId={systemId}
      autoReports={[2, 3]}
      progressCircles={[6, 8]}
    />
  );

  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <PageHeader
          topHeader={systemName}
          subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
        />
      </frames._page_inner_header>

      {gridReducerSecondary || gridReducerTertiary ? (
        <frames._page_inner_content_scroll>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gridReducerTertiary ? "1fr" : "320px 1fr",
              gridTemplateRows: gridReducerTertiary ? "1fr" : "150px 360px 1fr",
              height: "100%",
              gridGap: "20px",
              boxSizing: "border-box",
            }}
          >
            {gridReducerTertiary && (
              <div
                style={{
                  display: "flex",
                  gridArea: "1/1/2/2",
                  flexDirection: "column",
                  gap: "20px",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  scrollbarColor: colors.greyDarkPrimary + colors.transparent,
                  scrollbarWidth: "thin",
                  alignItems: "center",
                }}
              >
                {CardDisplay}
                <SystemControls system={systemId}/>
                <Attentions />
                <div
                  ref={widgetResizeRef}
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  {FeedinWidget}
                  {MilestonesWidget}
                </div>
              </div>
            )}

            {!gridReducerTertiary && (
              <>
                <div style={{ gridArea: "1/1/2/2" }}>{CardDisplay}</div>

                <div style={{ gridArea: "2/1/3/2" }}>
                  <SystemControls system={systemId}/>
                </div>

                <div style={{ gridArea: "1/2/3/3", overflow: "hidden" }}>
                  <Attentions />
                </div>

                <div
                  style={{
                    gridArea: "3/1/4/3",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <div
                    ref={widgetResizeRef}
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                      height: "100%",
                      alignItems: "center",
                    }}
                  >
                    {FeedinWidget}
                    {MilestonesWidget}
                  </div>
                </div>
              </>
            )}
          </div>
        </frames._page_inner_content_scroll>
      ) : (
        <frames._page_inner_content>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gridReducerPrimary
                ? "320px 1fr"
                : gridMaxOut
                ? "320px 1fr 380px"
                : "320px 1fr 210px",
              gridTemplateRows: gridReducerPrimary ? "1fr" : "1fr 360px",
              height: "100%",
              gridGap: gridReducerPrimary ? "0px 20px" : "10px 20px",
              boxSizing: "border-box",
            }}
          >
            {gridReducerPrimary && (
              <div
                style={{
                  display: "flex",
                  gridArea: "1/1/2/2",
                  flexDirection: "column",
                  gap: "20px",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  scrollbarColor: colors.greyDarkPrimary + colors.transparent,
                  scrollbarWidth: "thin",
                }}
              >
                {CardDisplay}
                <SystemControls system={systemId}/>
                <Attentions />
              </div>
            )}

            {!gridReducerPrimary && (
              <>
                {!hideCardNumberDisplays && (
                  <div style={{ gridArea: "1/1/2/2" }}>{CardDisplay}</div>
                )}
                <div
                  style={{
                    gridArea: hideCardNumberDisplays ? "1/1/3/2" : "2/1/3/2",
                    display: "flex",
                    alignItems: "end",
                  }}
                >
                  <SystemControls system={systemId}/>
                </div>

                <div style={{ gridArea: "1/3/3/4", overflow: "hidden" }}>
                  <Attentions />
                </div>
              </>
            )}

            <div
              style={{
                gridArea: gridReducerPrimary ? "1/2/4/3" : "1/2/3/3",
                padding: gridReducerPrimary ? "0px 0px 0px 20px" : "0px 20px",
                borderLeft: "2px solid " + colors.greyDarkPrimary,
                borderRight: gridReducerPrimary
                  ? "none"
                  : "2px solid " + colors.greyDarkPrimary,
                overflowY: "scroll",
                overflowX: "hidden",
                scrollbarColor: colors.greyDarkPrimary + colors.transparent,
                scrollbarWidth: "thin",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                ref={widgetResizeRef}
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                  height: "100%",
                  alignItems: "center",
                }}
              >
                {FeedinWidget}
                {MilestonesWidget}
              </div>
            </div>
          </div>
        </frames._page_inner_content>
      )}
    </frames.Page_inner>
  );
}
